import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {CombinedLobbyState} from '../model/CombinedLobbyState';
import createAxiosInstance from './AxiosInstance';
import {getResponseStatus, SESSION_EXPIRED_STATUS} from './alarmCoupling';
import {useStomp} from './useStomp';

interface LobbyStateOptions {
    lobbyId?: string;
    active: boolean;
    token: string | null;
    connectionStatus: ReturnType<typeof useStomp>['status'];
    subscribe: ReturnType<typeof useStomp>['subscribe'];
    onSessionExpired: () => void;
}

export const useLobbyState = (options: LobbyStateOptions) => {
    const {active, lobbyId, token, subscribe, connectionStatus} = options;
    const [state, setState] = useState<CombinedLobbyState>();
    const [error, setError] = useState<string>();
    const contextRef = useRef({revision: 0, request: 0, applied: 0});
    const requestsRef = useRef(new Set<AbortController>());
    const optionsRef = useRef(options);
    useLayoutEffect(() => { optionsRef.current = options; });

    useLayoutEffect(() => {
        contextRef.current = {revision: 0, request: 0, applied: 0};
        setState(undefined);
        setError(undefined);
        const requests = requestsRef.current;
        return () => { requests.forEach((controller) => controller.abort()); requests.clear(); };
    }, [options.lobbyId, options.token, options.active]);

    const refresh = useCallback(async (externalSignal?: AbortSignal) => {
        const {lobbyId, active, token, onSessionExpired} = optionsRef.current;
        if (!active || !lobbyId || !token || externalSignal?.aborted) return;
        const context = contextRef.current;
        const revision = context.revision;
        const requestId = ++context.request;
        const controller = new AbortController();
        const abort = () => controller.abort();
        externalSignal?.addEventListener('abort', abort, {once: true});
        requestsRef.current.add(controller);
        try {
            const response = await createAxiosInstance().get<CombinedLobbyState>(`/lobbies/${lobbyId}/state`, {
                signal: controller.signal,
            });
            if (controller.signal.aborted || context !== contextRef.current) return;
            if (context.revision !== revision || requestId < context.applied) return;
            context.applied = requestId;
            setState(response.data);
            setError(undefined);
        } catch (cause) {
            if (controller.signal.aborted || context !== contextRef.current) return;
            if (getResponseStatus(cause) === SESSION_EXPIRED_STATUS) onSessionExpired();
            setError('Lobbygegevens konden niet worden geladen. Probeer het opnieuw.');
            throw new Error('De actuele lobbystatus kon niet worden opgehaald.');
        } finally {
            requestsRef.current.delete(controller);
            externalSignal?.removeEventListener('abort', abort);
        }
    }, []);

    useEffect(() => {
        if (!active || !lobbyId || !token) return;
        const context = contextRef.current;
        return subscribe<CombinedLobbyState>(`/topic/dertigen/lobby/${lobbyId}`, (update) => {
            if (context !== contextRef.current) return;
            if (update.lobby.id !== Number(lobbyId)) return;
            context.revision += 1;
            setState(update);
            setError(undefined);
        });
    }, [active, lobbyId, token, subscribe]);

    useEffect(() => {
        void refresh().catch(() => undefined);
    }, [refresh, options.active, options.lobbyId, options.token]);

    useEffect(() => {
        if (connectionStatus === 'connected') void refresh().catch(() => undefined);
    }, [connectionStatus, refresh]);

    return {state, error, refresh};
};
